import React, { useState, useEffect, useCallback } from "react";
import { useParams } from 'react-router-dom';
import {
  Box,
  FormControl,
  FormLabel,
  Input,
  Text,
  Heading,
  Button,
  useToast,
  useColorModeValue,
  Flex,
  VStack,
  HStack,
  Badge,
  Select,
  Textarea,
  Switch,
} from "@chakra-ui/react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Product } from '../../Platform/Common/types';
import B2CPracticeSetManager from "./B2CPracticeSetManager";

interface TokenPayload {
    sub: string;
    role_id: number;
    tenant_id: number;
    exp?: number;
  }

  const B2CReviewProduct = () => {
    // ────────────────────────────────────────────────────────────────────────────
    // Theme & routing hooks
    // ────────────────────────────────────────────────────────────────────────────

    const cardbg = useColorModeValue("gray.50", "gray.700");
  
    const { product_id } = useParams<{ product_id: string }>();
    const [productId, setProductId] = useState<number>(Number(product_id));
    const navigate = useNavigate();
    const toast = useToast();

  
    // ────────────────────────────────────────────────────────────────────────────
    // State: core product, question-sets, mappings
    // ────────────────────────────────────────────────────────────────────────────

  
    const [product, setProduct] = useState<Product>({
        product_id: 0, 
        name: '', 
        current_content_type : "PRACTICE_SET",
        description: '', 
        is_active: true, 
        created_at: new Date(), 
        updated_at: new Date()
    });

  
    const [isProductEdited, setIsProductEdited] = useState(false);

    const [editSessionKey, setEditSessionKey] = useState(0);
  
  
    // ────────────────────────────────────────────────────────────────────────────
    // Mode & button logic
    // ────────────────────────────────────────────────────────────────────────────
    const [isEditing, setIsEditing] = useState(false);
    const isCreating = productId === 0;
    const isViewMode = !isEditing;

  
    const showActionButtons    = !isCreating;
    const showSaveButton       = isCreating || !isViewMode;
    const isUpdateButtonEnabled   = showActionButtons && isViewMode ;
 

  
    // ────────────────────────────────────────────────────────────────────────────
    // API fetchers
    // ────────────────────────────────────────────────────────────────────────────
    const fetchProduct = async () => {
      const b2c_apiurl = process.env.REACT_APP_B2C_API_URL || 'https://default-url.com';
      const { data } = await axios.get<Product>(`${b2c_apiurl}/product/${product_id}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("b2c_token")}` },
      });
      setProduct(data);
    };



    // ────────────────────────────────────────────────────────────────────────────
    // Derived state effects
    // ────────────────────────────────────────────────────────────────────────────

    const loadAllData = useCallback(async () => {
        const token = localStorage.getItem("b2c_token");
        if (!token) return;
      
        // 2) if editing an existing product
        if (Number(product_id) > 0) {
          await fetchProduct();

          setProductId(Number(product_id));
        }
     
        // 4) reset any “edited” flags
        setIsEditing(false);
        setIsProductEdited(false);
      }, [
        product_id,
        fetchProduct,
 
      ]);

    // 1) On load —  fetch everything
    useEffect(() => {
      (async () => {
        const token = localStorage.getItem("b2c_token");
        if (!token) return;
  
  
        if (Number(product_id) > 0) {
          await fetchProduct();

          setProductId(Number(product_id));
        }

      })();
    }, [product_id]);

  

  
    // ────────────────────────────────────────────────────────────────────────────
    // Form handlers
    // ────────────────────────────────────────────────────────────────────────────
const handleInputChange = (
  e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
) => {
            const { name, value, type } = e.target;

            const finalValue =
              type === "checkbox"
                ? (e.target as HTMLInputElement).checked
                : value;

  setProduct(prev => ({
    ...prev,
    [name]: finalValue,
  }));

  setIsProductEdited(true);
};


  
    // ────────────────────────────────────────────────────────────────────────────
    // Save / Create / Update / Publish / Cancel / Terminate
    // ────────────────────────────────────────────────────────────────────────────
    const handleCreateProduct = async () => {
      try {
        const b2c_apiurl = process.env.REACT_APP_B2C_API_URL || 'https://default-url.com';
        console.log("Practice Set", product);
        const { data } = await axios.post<Product>(`${b2c_apiurl}/create-product/`, product, {
          headers: { Authorization: `Bearer ${localStorage.getItem("b2c_token")}` },
        });
        const newId = data.product_id;
        // re-map question-sets

        toast({ title: "Product created", status: "success" });

        setIsProductEdited(false);

        navigate(`/practice/app/b2c-product-manager/review-b2c-product/${newId}`);
      
     } catch (err: any) {
    console.error(err);

        const message =
          err?.response?.data?.detail ||
          "Error creating product";

        toast({
          title: message,
          status: "error",
        });
      }
    };
  
    const handleUpdateProduct = async () => {
      const b2c_apiurl = process.env.REACT_APP_B2C_API_URL || 'https://default-url.com';
      // guard
      try {
        await axios.put(`${b2c_apiurl}/update-product/${product_id}`, product, {
          headers: { Authorization: `Bearer ${localStorage.getItem("b2c_token")}` },
        });
        toast({ title: "Product updated", status: "success" });
        setIsProductEdited(false);
        loadAllData();
     } catch (err: any) {
    console.error(err);

        const message =
          err?.response?.data?.detail ||
          "Error updating product";

        toast({
          title: message,
          status: "error",
        });
      }
    };

    
  
    const handleSaveProduct = () => {

      isCreating ? handleCreateProduct() : handleUpdateProduct();
    //  setIsProductEdited(false);
    };
  
    const handleUpdateClick = async () => {
        setIsEditing(true);
          // start a brand new edit session
        setEditSessionKey(prev => prev + 1);
    }


      const handleCancelOperation = () =>{
        setIsEditing(false);  
         loadAllData();
           // invalidate ALL child component state
        setEditSessionKey(prev => prev + 1);
        };

    

  return (
    <Box as="main" mx="auto" p={10} >

 {/* Product Details Top Bar */}
    <Box bg={useColorModeValue("gray.100", "gray.700")} p={4} borderRadius="sm" mb={4}>
    <Flex direction={["column", "row"]} justify="space-between" align={["flex-start", "center"]} wrap="wrap">
        <Heading size="lg">{product.name || "Untitled Product"}</Heading>
        <HStack spacing={2} mt={[2, 0]}>
        
        </HStack>
    </Flex>

    <Flex mt={3} wrap="wrap" fontSize="sm" color={useColorModeValue("gray.600", "gray.400")}>
        <Text mr={4}><strong>Created:</strong> {new Date(product.created_at).toLocaleDateString()}</Text>
        <Text><strong>Updated:</strong> {new Date(product.updated_at).toLocaleDateString()}</Text>
    </Flex>
    </Box>

{/* Operations Buttons */}
    <Box>
    {showActionButtons && (
        <HStack spacing={2} mt={4}>
            <Button
            size="sm"
            borderRadius="sm"
            onClick={handleUpdateClick}
            isDisabled={!isUpdateButtonEnabled}
            colorScheme="blue"
            >
            Update Product
            </Button>


          {isEditing && (
            <Button size={"sm"} variant="outline" colorScheme="red" onClick={handleCancelOperation}>
              Cancel
            </Button>
          )}

        </HStack>
        )}
    </Box>

    {/* Product Title & Description */}
{/* ===========================
   PRODUCT REVIEW CONTAINER
=========================== */}
    <Box mt={4}>

    {/* ===========================
        CORE INFORMATION
    =========================== */}
    <Box bgColor={cardbg} borderRadius="sm" p={4} boxShadow="md">
        <fieldset disabled={!showSaveButton}>
        <Text mb={4} fontSize="md" fontWeight="bold">
            Core Information
        </Text>

        <VStack spacing={4} align="stretch">

            {/* Title */}
            <FormControl id="name" isRequired>
            <Input
                name="name"
                placeholder="Product Title*"
                size="sm"
                value={product.name}
                onChange={handleInputChange}
            />
            </FormControl>

            <FormControl id="content_type" isRequired>
            <Select
                name="current_content_type"   // 👈 important
                size="sm"
                fontSize="sm"
                value={product.current_content_type}
                onChange={handleInputChange}
                placeholder="Select Content Type"
            >
                <option value="PRACTICE_SET">Practice Set</option>
                <option value="BOOK">Book</option>
                <option value="ONLINE_COURSE">Online Course</option>
            </Select>
            </FormControl>
                                

            {/* Description */}
            <FormControl id="description">
            <Textarea
                name="description"
                placeholder="Product Description"
                size="sm"
                value={product.description || ""}
                onChange={handleInputChange}
            />
            </FormControl>


            {/* Active Toggle */}
            <FormControl display="flex" alignItems="center">
            <FormLabel htmlFor="is_active" mb="0" fontSize="sm">
                Active
            </FormLabel>
            <Switch
                id="is_active"
                name="is_active"
                isChecked={product.is_active}
                onChange={handleInputChange}
            />
            </FormControl>

        </VStack>
        </fieldset>
    </Box>

        {/* Save Button */}
        {showSaveButton && (
        <Box textAlign="left" mt={4}>
            <Button
            mb={2}
            colorScheme="teal"
            size="sm"
            borderRadius="sm"
            onClick={handleSaveProduct}
            isDisabled={!isProductEdited}
            >
            Commit
            </Button>
        </Box>
        )}

    </Box>
    
          {/* Practice Set Manager Container */}
      <Box
       mt={4}
        bg={cardbg}
        p={2}
        borderRadius="md"
        borderWidth="1px"
        boxShadow="sm"
      >
        {productId > 0 && (product.current_content_type == "PRACTICE_SET") && (
          <B2CPracticeSetManager product_id={productId} />
        )}
      </Box>
      
    </Box>
  );
};

export default B2CReviewProduct;
